import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CONTENT_KEY_BIG_FIVE,
  DISC_TEST_ASSESSMENT_ID,
  ENNEAGRAM_TEST_ASSESSMENT_ID,
  MODEL_TYPE_CHILDHOOD_TRAUMA,
  PERSONALITY_TEST_ASSESSMENT_ID,
} from "../../utils/general-config";

import {
  CHILDHOOD_TRAUMA_OVERALL_LEVEL_RANGES,
  CHILDHOOD_TRAUMA_TRAITS,
  EMOTIONAL_HARM_RANGES,
  getLevelByRanges,
  PRESENT_DAY_EFFECTS_RANGES,
  TRAUMA_EXPOSURE_RANGES,
} from "../../utils/assessment-utils";
import {
  getEnneagramBannerImage,
  convertToLowerCase,
  getChildhoodPageImage,
} from "../../utils/utility-functions";
import Header from "../../components/header";
import BackToTopButton from "../../components/back-to-top/component";
import Loader from "../../components/loader";
import Footer from "../../components/footer";
import ShareButton from "../../components/share-button";
import SendToFriendButton from "../../components/send-to-friend-button";
import ExpandableSection from "../../components/expandable-collapsible/component";
import {
  APP_NAVIGATION,
  ASSESSMENT_BACKGROUND_COLORS,
  LOCALE,
} from "../../utils/constants";
import { useTranslation } from "react-i18next";
import InviteFriend from "../../components/invite-friend";
import { CallToActionSection } from "../../components/call-to-action/component";
import PathHelper from "../../utils/path-helper";

const ChildhoodTraumaReport = ({
  getAssessmentResultByAssessmentAttemptIdConnect,
  getInsightfulContentConnect,
  getUserByIdConnect,
  sendToFriendReportOnEmailConnect,
  funnelStep,
  getAssessmentConnect,
  getAssessmentAttemptByUserIdConnect,
  getGuestUserConnect,
  getTransactionDetailConnect,
  getBillingConnect,
  guestUserChangesConnect,
  isGuestOrPremiumUserConnect,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [contentData, setContentData] = useState({});
  const [userTraits, setUserTraits] = useState([]);
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const assessmentAttemptId = queryParams.get("aai");
  const [isLoading, setIsLoading] = useState(false);
  const [currentAssessmentAttemptData, setCurrentAssessmentAttemptData] =
    useState("");
  const { userGUID, isAuth, selectedPlan } = funnelStep || {};
  const [userData, setUserData] = useState(null);
  // const token = getCookie("jwtToken");
  const userId = queryParams.get("uid") || userGUID;
  const { locale } = useParams();
  const currentLocale = locale ?? LOCALE.EN;
  const pathSegments = location.pathname.split("/");
  const reportType = pathSegments[4];
  const assessmentId = currentAssessmentAttemptData?.assessmentId;
  const quizPath = PathHelper(APP_NAVIGATION.QUIZ, currentLocale);

  useEffect(() => {
    if (!assessmentAttemptId) return;

    const fetchAssessmentAndContent = async () => {
      setIsLoading(true); // Start loading
      try {
        // Fetch user traits
        const traitsResponse =
          await getAssessmentResultByAssessmentAttemptIdConnect(
            assessmentAttemptId,
            currentLocale,
          );
        if (traitsResponse) {
          setUserTraits(traitsResponse.userTraits);
          setCurrentAssessmentAttemptData(traitsResponse);
        }

        // Fetch content
        const contentResponse = await getInsightfulContentConnect({
          modelType:
            MODEL_TYPE_CHILDHOOD_TRAUMA[currentLocale] ||
            MODEL_TYPE_CHILDHOOD_TRAUMA[LOCALE.EN],

          contentKey: CONTENT_KEY_BIG_FIVE,
        });
        if (contentResponse) {
          const { content } = contentResponse;
          setContentData(content);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false); // End loading
      }
    };

    fetchAssessmentAndContent();
  }, [assessmentAttemptId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  useEffect(() => {
    if (userId && isAuth) {
      getUserByIdConnect(userId).then((res) => {
        if (res) {
          setUserData(res);
        }
      });
    }
  }, [userId, isAuth]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isLoading]);

  const bannerImage = getEnneagramBannerImage();
  const result1ImageText = "cht-result-1";
  const result1Image = getChildhoodPageImage(result1ImageText);
  const result2ImageText = "cht-result-2";
  const result2Image = getChildhoodPageImage(result2ImageText);
  const result3ImageText = "cht-result-3";
  const result3Image = getChildhoodPageImage(result3ImageText);
  const result4ImageText = "cht-result-4";
  const result4Image = getChildhoodPageImage(result4ImageText);

  const colorVariants = {
    purple: "from-purple-600 via-purple-500",
    teal: "from-teal-600 via-teal-500",
    blue: "from-blue-600 via-blue-500",
    red: "from-red-600 via-red-500",
    green: "from-green-600 via-green-500",
    yellow: "from-yellow-600 via-yellow-500",
    indigo: "from-indigo-600 via-indigo-500",
  };

  const TakeTestButton = ({ onClick, id }) => {
    return (
      <button
        className="flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full shadow-md border border-gray-300 hover:bg-gray-100 transition-all"
        onClick={onClick}
        id={id}
      >
        {t("report.adhd.take_test_cta")}
        <svg
          fill="black"
          width="20"
          height="20"
          viewBox="-2 -2 24 24"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin"
          className="jam jam-arrow-circle-right"
        >
          <path d="M10 20C4.477 20 0 15.523 0 10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1.586-7H6a1 1 0 0 1 0-2h5.586L9.05 6.464a1 1 0 1 1 1.414-1.414l4.243 4.243a.997.997 0 0 1 0 1.414l-4.243 4.243a1 1 0 1 1-1.414-1.414L11.586 11z" />
        </svg>
      </button>
    );
  };

   const HeroBanner = ({ title, color = "purple", buttonNeeded, onClick }) => {
     const gradientClasses = colorVariants[color] || colorVariants.purple;

     return (
       <div
         className={`relative h-auto bg-gradient-to-r ${gradientClasses} to-purple-600`}
       >
         {/* Background Image (Hidden on small screens) */}
         <img
           src={bannerImage}
           alt={title}
           className="absolute bottom-0 right-0 w-full md:w-2/3 h-full object-cover object-right"
         />

         {/* Gradient Overlay */}
         <div
           className={`block absolute inset-0 bg-gradient-to-r ${gradientClasses} to-transparent`}
         />
         <div className="absolute inset-0 bg-black opacity-10" />

         {/* Hero Content */}
         <div className="relative h-full flex items-center" id={reportType}>
           <div className="w-full px-4 py-8 lg:px-8 lg:py-12" id={reportType}>
             {/* Title & Button Wrapper */}
             <div className="flex flex-col md:flex-row items-start justify-between gap-4 pr-10 md:pr-16">
               {/* Title */}
               <h2
                 id={reportType}
                 style={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}
                 className="text-3xl md:text-4xl font-bold text-white break-words whitespace-pre-wrap text-left"
               >
                 {title}
               </h2>

               {/* Button (if needed) */}
               {buttonNeeded && (
                 <div className="flex-shrink-0">
                   <TakeTestButton onClick={onClick} t={t} />
                 </div>
               )}
             </div>
           </div>
         </div>
       </div>
     );
   };
  const InfoCard = ({ heading, items, bgClr }) => {
    return (
      <div
        className={`bg-${bgClr}-50 p-4 md:p-6 rounded-lg shadow-md border border-green-50 w-full h-full flex flex-col`}
      >
        <h2 className="font-bold text-lg text-gray-900">{heading}</h2>
        <ul className="list-disc list-outside pl-5 text-gray-700 mt-4">
          {items.map((item, index) => (
            <li key={index} className="break-words">
              {item}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const CallToActionSectionForUnAuth = ({ data }) => {
    return (
      <section className="space-y-6">
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.personality.personality_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.personality.personality_type_learn_more")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              id={"likeability-report-take-test-cta"}
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${PERSONALITY_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
        <div className="block bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 flex flex-col justify-center items-center gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.enneagram.enneagram_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.enneagram.enneagram_self_awareness")}
            </p>
            <button
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              id={"likeability-report-take-enneagram-test-cta"}
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${ENNEAGRAM_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-orange-600 to-yellow-600 p-8 justify-center items-center flex flex-col gap-2">
            <h3 className="text-xl font-bold text-white text-center">
              {t("report.disc_personality.disc_test_invitation")}
            </h3>
            <p className="text-white opacity-90 text-center">
              {t("report.disc_personality.take_assessment_cta")}
            </p>
            <button
              id={"likeability-report-take-disc-test-cta"}
              s
              className="bg-white text-indigo-600 px-6 py-2 rounded-full font-semibold hover:bg-indigo-100 transition-colors w-fit"
              onClick={() => {
                navigate(
                  `${quizPath.next}?assessmentId=${DISC_TEST_ASSESSMENT_ID}&newt=true`,
                );
              }}
            >
              {t("welcomeModal.take_the_test")}
            </button>
          </div>
        </div>
      </section>
    );
  };

  const InfoListCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    className = "",
  }) => {
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className={`p-4 md:p-6 rounded-2xl shadow-lg bg-[${bgClr}] flex flex-col h-full ${className}`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                    <CircleIcon character={character} size={20} />
                  </div>
                )}
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 flex items-center">
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}

        {overview && (
          <p className={`text-lg text-gray-700 ${contentArray ? `mb-4` : ""}`}>
            {overview}
          </p>
        )}
        {contentArray && (
          <ul className="space-y-2 mt-4 pl-6 md:pl-10">
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]">
                    <CircleIcon character={""} size={16} />
                  </div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };
  const CircleIcon = ({ character = "1", size = 24, color = "#7D5FFF" }) => {
    const viewBoxSize = 100;
    const centerPoint = viewBoxSize / 2;

    const minSize = 16;
    const actualSize = Math.max(size, minSize);
    const isBullet = character === "";
    const isQuestion = character === "?";
    const isNumber = !isNaN(parseInt(character));
    let finalColor;

    if (color !== "#7D5FFF") {
      finalColor = "#336bcc";
    } else {
      if (isQuestion) {
        finalColor = "#805bec";
      } else if (isNumber) {
        finalColor = "#805bec";
      } else {
        finalColor = "#805bec";
      }
    }

    const textSize = isQuestion ? 60 : 50;

    return (
      <svg
        width={actualSize}
        height={actualSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        className="min-w-[16px] min-h-[16px]"
        style={{ filter: "drop-shadow(0 0 5px rgba(0,0,0,0.3))" }}
      >
        <circle
          cx={centerPoint}
          cy={centerPoint}
          r={isBullet ? 42 : 48}
          fill={finalColor}
          stroke="none"
        />

        <text
          x={centerPoint}
          y={centerPoint + (isQuestion ? 4 : 2)}
          fontSize={textSize}
          fontWeight="bold"
          fill="white"
          textAnchor="middle"
          dominantBaseline="middle"
          fontFamily="Arial, sans-serif"
        >
          {character}
        </text>
      </svg>
    );
  };
  const DescriptiveCard = ({
    heading,
    bgClr,
    contentArray,
    overview,
    character,
    isSubHeading = false,
    textClr,
    level,
  }) => {
    return (
      <div
        className={`p-4 md:p-6 rounded-2xl shadow-lg ${bgClr} flex flex-col h-full`}
      >
        {heading && (
          <div className="flex items-start gap-2 mb-2">
            <div>
              <div className="flex items-start gap-2">
                {character && (
                  <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                    {" "}
                  </div>
                )}

                <h2
                  className={`text-lg sm:text-xl ${textClr}  font-semibold  flex items-center`}
                >
                  {heading}
                </h2>
              </div>
            </div>
          </div>
        )}
        {level && (
          <div
            className={`text-sm  sm:text-lg font-semibold ${textClr} bg-opacity-30`}
          >
            {t("report.childhood_trauma.level")}: &nbsp;
            {level}
          </div>
        )}

        {overview && (
          <p
            className={`whitespace-pre-line text-lg text-gray-700 mt-4 ${
              character ? "pl-8 md:pl-10" : ""
            } ${contentArray ? `mb-4` : ""}`}
          >
            {overview}
          </p>
        )}
        {contentArray && (
          <ul
            className={`space-y-2 mt-4 ${
              overview ? "pl-8 md:pl-12" : "md:pl-6 pl-2"
            }`}
          >
            {contentArray?.map((item, index) => {
              return (
                <li key={index} className="flex gap-4">
                  {/* Left Section: Circle Icon */}
                  <div className="w-4 h-4 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-[5px]"></div>

                  {/* Right Section: Heading & Content */}
                  <div className="flex flex-col">
                    {isSubHeading ? (
                      <>
                        {/* Heading and First Line in Same Row */}
                        <div className="flex items-center gap-2">
                          <h2 className="text-lg font-semibold text-gray-700">
                            {item?.heading}
                          </h2>
                        </div>

                        {/* Paragraph with text-indent effect */}
                        <p className="text-gray-700 text-base md:text-lg leading-relaxed relative">
                          <span className="">{item.content}</span>
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                        {item}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    );
  };

  const NextStepsCard = ({
    heading,
    intro,
    bgClr,
    contentArray,
    overview,
    character,
  }) => {
    return (
      <div
        style={{ backgroundColor: bgClr }}
        className="p-4 md:p-6 rounded-2xl shadow-lg flex flex-col h-full"
      >
        {/* HEADING */}
        {heading && (
          <div className="flex items-start gap-2 mb-3">
            {character && (
              <div className="shrink-0 flex items-center justify-center rounded-full border-4 border-white shadow-[0_0_5px_rgba(0,0,0,1)]">
                <CircleIcon character={character} size={20} />
              </div>
            )}

            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                {heading}
              </h2>

              {/* OPTIONAL INTRO */}
              {intro && (
                <p className="text-gray-600 text-sm md:text-base mt-2 leading-relaxed">
                  {intro}
                </p>
              )}
            </div>
          </div>
        )}

        {/* OVERVIEW */}
        {overview && (
          <p className="text-gray-700 text-base md:text-lg mb-4">{overview}</p>
        )}

        {/* CONTENT LIST */}
        {contentArray && (
          <ul className="space-y-4 mt-2">
            {contentArray.map((item, index) => (
              <li key={index} className="flex gap-3 items-start">
                {/* BULLET */}
                <div className="w-4 h-4 flex-shrink-0 flex items-center justify-center rounded-full border-2 border-white shadow-[0_0_5px_rgba(0,0,0,1)] mt-1">
                  <CircleIcon character={""} size={16} />
                </div>

                {/* TEXT / LINK */}
                <div className="flex flex-col">
                  {/* Optional Heading */}
                  {item?.Heading && (
                    <h3 className="text-base font-semibold text-gray-700">
                      {item.Heading}
                    </h3>
                  )}

                  {/* Content (Clickable if link exists) */}
                  {item?.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-700 text-base md:text-lg leading-relaxed hover:text-blue-600 hover:underline transition break-words"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <p className="text-gray-700 text-base md:text-lg leading-relaxed break-words">
                      {item.label}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const traitMap = Object.fromEntries(
    userTraits.map((trait) => [trait.traitId.toLowerCase(), trait]),
  );

  const childhoodTraumaTraitsData = {
    presentDayEffects:
      traitMap[CHILDHOOD_TRAUMA_TRAITS.PRESENT_DAY_EFFECTS.toLowerCase()],
    emotionalHarm:
      traitMap[CHILDHOOD_TRAUMA_TRAITS.EMOTIONAL_HARM.toLowerCase()],
    physicalSafetyAndBoundaries:
      traitMap[
        CHILDHOOD_TRAUMA_TRAITS.PHYSICAL_SAFETY_AND_BOUNDARIES.toLowerCase()
      ],
    householdInstabilityFromAdultImpairment:
      traitMap[
        CHILDHOOD_TRAUMA_TRAITS.HOUSEHOLD_INSTABILITY_FROM_ADULT_IMPAIRMENT.toLowerCase()
      ],
    householdDangerAndDisruption:
      traitMap[
        CHILDHOOD_TRAUMA_TRAITS.HOUSEHOLD_DANGER_AND_DISRUPTION.toLowerCase()
      ],
  };
  const overallScore =
    childhoodTraumaTraitsData.emotionalHarm?.score +
      childhoodTraumaTraitsData.physicalSafetyAndBoundaries?.score +
      childhoodTraumaTraitsData.householdInstabilityFromAdultImpairment?.score +
      childhoodTraumaTraitsData.householdDangerAndDisruption?.score || 0;
  const simplifiedOverallScore = Math.round(overallScore / 4); // Convert to a 0-4 scale

  const childhoodTraumaTraitLabels = {
    emotionalHarm: getLevelByRanges(
      childhoodTraumaTraitsData.emotionalHarm?.score ?? 0,
      EMOTIONAL_HARM_RANGES,
    ),

    physicalSafetyAndBoundaries: getLevelByRanges(
      childhoodTraumaTraitsData.physicalSafetyAndBoundaries?.score ?? 0,
      TRAUMA_EXPOSURE_RANGES,
    ),

    householdInstabilityFromAdultImpairment: getLevelByRanges(
      childhoodTraumaTraitsData.householdInstabilityFromAdultImpairment
        ?.score ?? 0,
      TRAUMA_EXPOSURE_RANGES,
    ),

    householdDangerAndDisruption: getLevelByRanges(
      childhoodTraumaTraitsData.householdDangerAndDisruption?.score ?? 0,
      TRAUMA_EXPOSURE_RANGES,
    ),

    presentDayEffects: getLevelByRanges(
      childhoodTraumaTraitsData.presentDayEffects?.score ?? 0,
      PRESENT_DAY_EFFECTS_RANGES,
    ),

    overallScore: getLevelByRanges(
      simplifiedOverallScore,
      CHILDHOOD_TRAUMA_OVERALL_LEVEL_RANGES,
    ),
  };

  return (
    <>
      <Header />
      <BackToTopButton />
      <div className="min-h-screen">
        <div className="container mx-auto p-4 sm:p-8">
          {isLoading ? (
            <div className="flex justify-center items-center h-screen w-full">
              <Loader isLoading={isLoading} />
            </div>
          ) : (
            <>
              {/* SECTION 1 - Test Results */}
              <section className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  defaultExpanded={true}
                  heroBanner={
                    <HeroBanner title={contentData?.header} color="blue" />
                  }
                >
                  {isAuth && (
                    <div className="w-full pt-[10px] flex justify-end lg:justify-end gap-[10px] lg:px-6">
                      <ShareButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                      />
                      <SendToFriendButton
                        currentAssessmentAttemptData={
                          currentAssessmentAttemptData
                        }
                        userGUID={userGUID}
                        userData={userData}
                        CreateGuestUserConnect={
                          sendToFriendReportOnEmailConnect
                        }
                      />
                    </div>
                  )}
                  <div className="w-full p-6 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 items-start">
                      {/* LEFT - ABOUT */}
                      <div className="flex flex-col h-full">
                        <h3 className="text-lg md:text-xl font-semibold text-gray-700">
                          {t("report.childhood_trauma.overview")}
                        </h3>
                        <div className="mt-8">
                          <p className="whitespace-pre-line text-gray-700 text-base md:text-lg leading-relaxed">
                            {contentData?.intro}
                          </p>
                        </div>
                      </div>

                      {/* TRAIT SCORE */}
                      <div className="flex flex-col h-full">
                        <h3 className="text-lg md:text-xl font-semibold text-gray-700">
                          {contentData?.overallResult?.heading}
                        </h3>
                        <div className="flex items-center gap-4 w-full max-w-md">
                          {/* Score Text */}
                          <h3 className="text-lg md:text-xl font-semibold text-gray-700 whitespace-nowrap">
                            {t("report.childhood_trauma.score")}
                          </h3>

                          {/* Progress Bar + Value */}
                          <div className="flex items-center gap-3 flex-1">
                            <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                              <div
                                className="bg-pink-500 h-full rounded-full"
                                style={{
                                  // The overall score is averaged to fit into a 0-4 scale, which is then converted to a percentage for the progress bar.
                                  width: `${(simplifiedOverallScore / 7) * 100}%`,
                                }} // Convert to percentage
                              />
                            </div>

                            <span className="text-base font-semibold text-gray-700">
                              {simplifiedOverallScore}/7
                            </span>
                          </div>
                        </div>
                        <div className="mt-8">
                          <h3 className="whitespace-pre-line text-gray-700 text-base md:text-lg leading-relaxed">
                            {
                              contentData?.overallResult?.bands[
                                childhoodTraumaTraitLabels.overallScore
                              ]?.label
                            }
                          </h3>
                          <p className="whitespace-pre-line text-gray-700 text-base md:text-lg leading-relaxed">
                            {
                              contentData?.overallResult?.bands[
                                childhoodTraumaTraitLabels.overallScore
                              ]?.description
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              {/* SECTION 2 - How is this Scored */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-4">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.howScored?.heading}
                      color="teal"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="relative px-4">
                      <img
                        src={result1Image}
                        alt="Banner Image"
                        className="hidden lg:block float-right w-[38%] max-w-md ml-6 mb-1"
                      />

                      <div
                        className="whitespace-pre-line text-gray-700 text-base md:text-lg leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: contentData?.howScored?.content || "",
                        }}
                      />
                    </div>

                    {/* Mobile image */}
                    <div className="flex justify-center mt-4 lg:hidden">
                      <img
                        src={result1Image}
                        alt="Banner Image"
                        className="w-full max-w-md object-contain"
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              {/* SECTION 3 - UNDERSTANDING RESULTS */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.understandingResults?.heading}
                      color="red"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-4 lg:items-end">
                      <div className="px-4 w-full max-w-full overflow-hidden">
                        <div
                          className="whitespace-pre-line text-gray-700 text-base md:text-lg leading-relaxed"
                          dangerouslySetInnerHTML={{
                            __html:
                              contentData?.understandingResults?.content || "",
                          }}
                        />
                      </div>
                      <div className="flex flex-col justify-end">
                        <img
                          src={result2Image}
                          alt="Banner Image"
                          className="w-full max-w-md mx-auto object-contain "
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              {/* SECTION 4 - Adversity */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.adversityProfile?.heading}
                      color="green"
                    />
                  }
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="w-full">
                      <DescriptiveCard
                        heading={
                          contentData?.adversityProfile?.buckets.emotionalHarm
                            .label
                        }
                        overview={
                          contentData?.adversityProfile?.buckets.emotionalHarm
                            .bands[childhoodTraumaTraitLabels.emotionalHarm]
                            ?.description
                        }
                        level={
                          contentData?.adversityProfile?.buckets.emotionalHarm
                            .bands[childhoodTraumaTraitLabels.emotionalHarm]
                            ?.label
                        }
                        bgClr={"bg-orange-100"}
                      />
                    </div>

                    <div className="w-full">
                      <DescriptiveCard
                        heading={
                          contentData?.adversityProfile?.buckets
                            ?.physicalSafetyAndBoundaries?.label
                        }
                        overview={
                          contentData?.adversityProfile?.buckets
                            .physicalSafetyAndBoundaries.bands[
                            childhoodTraumaTraitLabels
                              .physicalSafetyAndBoundaries
                          ]?.description
                        }
                        level={
                          contentData?.adversityProfile?.buckets
                            .physicalSafetyAndBoundaries.bands[
                            childhoodTraumaTraitLabels
                              .physicalSafetyAndBoundaries
                          ]?.label
                        }
                        bgClr={"bg-yellow-100"}
                      />
                    </div>

                    <div className="w-full">
                      <DescriptiveCard
                        heading={
                          contentData?.adversityProfile?.buckets
                            .householdInstabilityFromAdultImpairment.label
                        }
                        overview={
                          contentData?.adversityProfile?.buckets
                            .householdInstabilityFromAdultImpairment.bands[
                            childhoodTraumaTraitLabels
                              .householdInstabilityFromAdultImpairment
                          ]?.description
                        }
                        level={
                          contentData?.adversityProfile?.buckets
                            .householdInstabilityFromAdultImpairment.bands[
                            childhoodTraumaTraitLabels
                              .householdInstabilityFromAdultImpairment
                          ]?.label
                        }
                        bgClr={"bg-pink-100"}
                      />
                    </div>

                    <div className="w-full">
                      <DescriptiveCard
                        heading={
                          contentData?.adversityProfile?.buckets
                            ?.householdDangerAndDisruption?.label
                        }
                        overview={
                          contentData?.adversityProfile?.buckets
                            .householdDangerAndDisruption.bands[
                            childhoodTraumaTraitLabels
                              .householdDangerAndDisruption
                          ]?.description
                        }
                        level={
                          contentData?.adversityProfile?.buckets
                            .householdDangerAndDisruption.bands[
                            childhoodTraumaTraitLabels
                              .householdDangerAndDisruption
                          ]?.label
                        }
                        bgClr={"bg-orange-100"}
                      />
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              {/* SECTION 5 - Present Day Effect */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.presentDayEffects?.heading}
                      color="orange"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-4 items-stretch">
                      <div className="px-4 w-full max-w-full overflow-hidden">
                        <div className="flex flex-col h-full">
                          <h3 className="whitespace-pre-line text-gray-700 text-base md:text-lg leading-relaxed">
                            {t("report.childhood_trauma.level")}
                            {
                              contentData?.presentDayEffects?.bands[
                                childhoodTraumaTraitLabels.presentDayEffects
                              ]?.label
                            }
                          </h3>
                          <div className="flex items-center gap-4 w-full max-w-md">
                            {/* Score Text */}
                            <h3 className="text-lg md:text-xl font-semibold text-gray-700 whitespace-nowrap">
                              {t("report.childhood_trauma.score")}
                            </h3>

                            {/* Progress Bar + Value */}
                            <div className="flex items-center gap-3 flex-1">
                              <div className="flex-1 bg-gray-200 rounded-full h-3 overflow-hidden">
                                <div
                                  className="bg-pink-500 h-full rounded-full"
                                  style={{
                                    width: `${
                                      childhoodTraumaTraitsData
                                        .presentDayEffects?.score
                                    }%`,
                                  }} // 4/4 = 100%
                                />
                              </div>

                              <span className="text-base font-semibold text-gray-700">
                                {
                                  childhoodTraumaTraitsData.presentDayEffects
                                    ?.score
                                }
                                /40
                              </span>
                            </div>
                          </div>
                          <div className="mt-8">
                            <p className="whitespace-pre-line text-gray-700 text-base md:text-lg leading-relaxed">
                              {
                                contentData?.presentDayEffects?.bands[
                                  childhoodTraumaTraitLabels.presentDayEffects
                                ]?.description
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col justify-end ">
                        <img
                          src={result3Image}
                          alt="Banner Image"
                          className="w-full max-w-md mx-auto object-contain "
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              {/* SECTION 6 - What This means in Everyday Life */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.everydayLife?.heading}
                      color="indigo"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-[60%_40%] gap-4 items-start">
                      <div className="space-y-3">
                        {contentData?.everydayLife?.bands?.[
                          childhoodTraumaTraitLabels.presentDayEffects
                        ]?.map((text, index) => (
                          <p
                            key={index}
                            className="whitespace-pre-line text-gray-700 text-base md:text-lg leading-relaxed"
                          >
                            {text}
                          </p>
                        ))}
                      </div>
                      <div className="flex items-cente items-end ">
                        <img
                          src={result4Image}
                          alt="Banner Image"
                          className="w-full max-w-md mx-auto object-contain "
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              {/* SECTION 7 - Support */}
              <section className="rounded-xl shadow-lg overflow-hidden mb-8">
                <ExpandableSection
                  heroBanner={
                    <HeroBanner
                      title={contentData?.seekHelp?.heading}
                      color="yellow"
                    />
                  }
                >
                  <div className="w-full p-4 md:px-8 md:py-6">
                    <div className="grid grid-cols-1 lg:grid-cols-[50%_50%] gap-4 items-start">
                      <div className="px-4 w-full max-w-full overflow-hidden">
                        <p className="whitespace-pre-line text-gray-700 text-base md:text-lg leading-relaxed">
                          {contentData?.seekHelp?.base}
                        </p>
                        <p className="whitespace-pre-line text-gray-700 text-base md:text-lg leading-relaxed">
                          {
                            contentData?.seekHelp?.addendums?.[
                              childhoodTraumaTraitLabels.presentDayEffects
                            ]
                          }
                        </p>
                      </div>

                      <div className="flex flex-col gap-4">
                        <NextStepsCard
                          heading={contentData?.resources?.heading}
                          intro={contentData?.resources?.intro}
                          character={"i"}
                          bgClr="#eff4fd"
                          contentArray={contentData?.resources?.items}
                        />

                        <DescriptiveCard
                          heading={contentData?.disclaimer?.heading}
                          overview={contentData?.disclaimer?.content}
                          bgClr="bg-orange-100"
                          textClr="text-yellow-800"
                        />
                      </div>
                    </div>
                  </div>
                </ExpandableSection>
              </section>

              {isAuth ? (
                <CallToActionSection
                  currentLocale={currentLocale}
                  t={t}
                  getAssessmentConnect={getAssessmentConnect}
                  getAssessmentAttemptByUserIdConnect={
                    getAssessmentAttemptByUserIdConnect
                  }
                  assessmentId={assessmentId}
                  userId={userGUID}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.BLUE_INDIGO}
                />
              ) : (
                CallToActionSectionForUnAuth({ contentData })
              )}

              {isAuth && (
                <InviteFriend
                  sectionMode={true}
                  getGuestUserConnect={getGuestUserConnect}
                  getTransactionDetailConnect={getTransactionDetailConnect}
                  getBillingConnect={getBillingConnect}
                  guestUserChangesConnect={guestUserChangesConnect}
                  isGuestOrPremiumUserConnect={isGuestOrPremiumUserConnect}
                  userId={userGUID}
                  userEmail={userData?.email}
                  buttonText={t("dashboard.invite_friend_cta")}
                  selectedPlan={selectedPlan}
                  assessmentData={currentAssessmentAttemptData}
                  showRemainingInvites={true}
                  bgColor={ASSESSMENT_BACKGROUND_COLORS.PURPLE_PINK}
                />
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ChildhoodTraumaReport;
